import { useEffect, useMemo, useState } from "react";
import { currency, slug } from "../lib/format.js";

export function TeamTreeTable({ rows, onOpenEmployee, onAssignTarget, onAddExpense }) {
  const { visibleRows, expandableIds, childIdsByParent } = useMemo(() => buildTreeRows(rows), [rows]);
  const [expandedIds, setExpandedIds] = useState(() => new Set(expandableIds));
  const hasActions = Boolean(onAssignTarget || onAddExpense);

  useEffect(() => {
    setExpandedIds(new Set(expandableIds));
  }, [expandableIds.join("|")]);

  if (!rows?.length) return <div className="empty">No team reporting data available yet.</div>;

  const expandedCount = expandedIds.size;
  function toggleRow(teamId) {
    setExpandedIds((current) => {
      const next = new Set(current);
      if (next.has(teamId)) {
        next.delete(teamId);
      } else {
        next.add(teamId);
      }
      return next;
    });
  }

  return (
    <>
      <div className="table-toolbar">
        <button className="ghost-btn compact-btn" type="button" onClick={() => setExpandedIds(new Set(expandableIds))}>
          Expand All
        </button>
        <button className="ghost-btn compact-btn" type="button" onClick={() => setExpandedIds(new Set())} disabled={!expandedCount}>
          Collapse All
        </button>
      </div>
      <div className="table-wrap">
        <table className="team-tree-table">
          <thead>
            <tr>
              <th>Team</th>
              <th>Reports To</th>
              <th>Manager / Role</th>
              <th>Health</th>
              <th>Child Teams</th>
              <th>Reps</th>
              <th>Leads</th>
              <th>Pipeline</th>
              <th>Follow-ups</th>
              <th>Calls</th>
              <th>Visits</th>
              <th>C/V</th>
              <th>Orders</th>
              <th>Converted</th>
              <th>Lost</th>
              <th>Conv %</th>
              <th>Achieved</th>
              <th>Target</th>
              <th>Gap</th>
              <th>AOV</th>
              <th>Revenue / Rep</th>
              <th>Progress</th>
              {hasActions ? <th>Action</th> : null}
            </tr>
          </thead>
          <tbody>
            {visibleRows
              .filter((row) => isVisible(row, expandedIds))
              .map((row) => {
                const children = childIdsByParent.get(row.nodeId) || [];
                const expanded = expandedIds.has(row.nodeId);
                const label = row.rowType === "member" ? row.name : row.teamName;
                return (
                  <tr key={row.nodeId} data-team-id={row.teamId} data-row-type={row.rowType}>
                    <td>
                      <div className={`tree-team-cell ${row.rowType === "member" ? "tree-member-cell" : ""}`} style={{ "--tree-level": row.level }}>
                        {children.length ? (
                          <button
                            className="tree-toggle"
                            type="button"
                            aria-label={`${expanded ? "Collapse" : "Expand"} ${label}`}
                            aria-expanded={expanded}
                            onClick={() => toggleRow(row.nodeId)}
                          >
                            {expanded ? "-" : "+"}
                          </button>
                        ) : (
                          <span className="tree-toggle-placeholder" />
                        )}
                        <div>
                          {row.rowType === "member" && onOpenEmployee ? (
                            <button className="table-link-btn" type="button" onClick={() => onOpenEmployee(row.userId)}>
                              {label}
                            </button>
                          ) : <strong>{label}</strong>}
                          <span className="subtle">{row.metaLine}</span>
                        </div>
                      </div>
                    </td>
                    <td>{row.parentTeamName || "Root"}</td>
                    <td>
                      {row.rowType === "team" && row.managerId && onOpenEmployee ? (
                        <button className="table-link-btn" type="button" onClick={() => onOpenEmployee(row.managerId)}>
                          {row.managerName || "Unassigned"}
                        </button>
                      ) : row.managerName || "Unassigned"}
                    </td>
                    <td>
                      <span className={`performance-status ${slug(row.status)}`}>{row.status || "Watch"}</span>
                    </td>
                    <td>{row.childTeams ?? 0}</td>
                    <td>{row.salesReps ?? 0}</td>
                    <td>{row.leads ?? 0}</td>
                    <td>{row.activePipeline ?? 0}</td>
                    <td>{row.followUpsDue ?? 0}</td>
                    <td>{row.calls ?? 0}</td>
                    <td>{row.visits ?? 0}</td>
                    <td>{row.callVisitRatio ?? 0}</td>
                    <td>{row.orders ?? 0}</td>
                    <td>{row.converted ?? 0}</td>
                    <td>{row.lost ?? 0}</td>
                    <td>{row.conversionRate ?? 0}%</td>
                    <td>{currency(row.achievedRevenue ?? 0)}</td>
                    <td>{currency(row.revenueTarget ?? 0)}</td>
                    <td>{currency(row.pendingRevenueGap ?? 0)}</td>
                    <td>{currency(row.averageOrderValue ?? 0)}</td>
                    <td>{currency(row.revenuePerRep ?? 0)}</td>
                    <td>
                      <div className="progress-cell">
                        <strong>{row.achievementPercent ?? 0}%</strong>
                        <div className="progress-track" aria-label={`${label} achievement ${row.achievementPercent ?? 0}%`}>
                          <div className="progress-fill" style={{ width: `${Math.min(100, row.achievementPercent ?? 0)}%` }} />
                        </div>
                      </div>
                    </td>
                    {hasActions ? (
                      <td>
                        <RollupActions row={row} onAssignTarget={onAssignTarget} onAddExpense={onAddExpense} />
                      </td>
                    ) : null}
                  </tr>
                );
              })}
          </tbody>
        </table>
      </div>
    </>
  );
}

function RollupActions({ row, onAssignTarget, onAddExpense }) {
  const canUseSalesActions = row.rowType === "member" && row.role === "sales" && row.active !== false;
  if (!canUseSalesActions) return <span className="subtle">-</span>;

  return (
    <div className="row-actions">
      {onAssignTarget ? (
        <button className="mini-btn" type="button" onClick={() => onAssignTarget(row, targetDraftFromMember(row))}>
          Assign Target
        </button>
      ) : null}
      {onAddExpense ? (
        <button className="mini-btn" type="button" onClick={() => onAddExpense(row, expenseDraftFromMember(row))}>
          Add Expense
        </button>
      ) : null}
    </div>
  );
}

function targetDraftFromMember(row) {
  return {
    productCategory: row.productCategory || "All",
    period: row.period || "Monthly",
    revenueTarget: Number(row.revenueTarget || 0),
    quantityTarget: Number(row.quantityTarget || 0),
  };
}

function expenseDraftFromMember(row) {
  return {
    purpose: `${row.name || "Sales rep"} performance follow-up`,
  };
}

function buildTreeRows(rows = []) {
  const teamRows = rows.map((row) => ({
    ...row,
    rowType: "team",
    nodeId: row.teamId,
    metaLine: row.teamId,
  }));
  const rowById = new Map(teamRows.map((row) => [row.nodeId, row]));
  const childTeamIdsByParent = new Map();
  const childIdsByParent = new Map();
  const memberRowsByTeamId = new Map();
  const rootIds = [];

  for (const row of teamRows) {
    const parentId = row.parentTeamId || "";
    if (!parentId || !rowById.has(parentId)) {
      rootIds.push(row.teamId);
    } else {
      childTeamIdsByParent.set(parentId, [...(childTeamIdsByParent.get(parentId) || []), row.teamId]);
    }

    const memberRows = (row.members || []).map((member) => ({
      ...member,
      rowType: "member",
      nodeId: `${row.teamId}:member:${member.userId}`,
      teamId: row.teamId,
      teamName: row.teamName,
      parentTeamId: row.teamId,
      parentTeamName: row.teamName,
      managerName: member.roleLabel,
      childTeams: 0,
      metaLine: `${member.roleLabel}${member.territory ? ` · ${member.territory}` : ""}${member.active === false ? " · Inactive" : ""}`,
    }));
    memberRowsByTeamId.set(
      row.teamId,
      memberRows.sort((a, b) => a.name.localeCompare(b.name)),
    );
    for (const memberRow of memberRows) rowById.set(memberRow.nodeId, memberRow);
  }

  for (const [parentId, childIds] of childTeamIdsByParent) {
    childTeamIdsByParent.set(parentId, childIds.sort((a, b) => rowById.get(a).teamName.localeCompare(rowById.get(b).teamName)));
  }

  for (const row of teamRows) {
    const childTeamIds = childTeamIdsByParent.get(row.teamId) || [];
    const memberIds = (memberRowsByTeamId.get(row.teamId) || []).map((member) => member.nodeId);
    if (childTeamIds.length || memberIds.length) {
      childIdsByParent.set(row.teamId, [...childTeamIds, ...memberIds]);
    }
  }

  const visibleRows = [];
  const sortedRootIds = rootIds.sort((a, b) => rowById.get(a).teamName.localeCompare(rowById.get(b).teamName));
  for (const rootId of sortedRootIds) {
    appendRow(rootId, 0, []);
  }

  function appendRow(teamId, level, ancestorIds) {
    const row = rowById.get(teamId);
    if (!row) return;
    visibleRows.push({ ...row, level, ancestorIds });
    for (const childId of childTeamIdsByParent.get(teamId) || []) {
      appendRow(childId, level + 1, [...ancestorIds, teamId]);
    }
    for (const memberRow of memberRowsByTeamId.get(teamId) || []) {
      visibleRows.push({ ...memberRow, level: level + 1, ancestorIds: [...ancestorIds, teamId] });
    }
  }

  return {
    visibleRows,
    expandableIds: [...childIdsByParent.keys()],
    childIdsByParent,
  };
}

function isVisible(row, expandedIds) {
  return row.ancestorIds.every((teamId) => expandedIds.has(teamId));
}
